import React, { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from '@tanstack/react-router'
import { Star, MessageSquare, Loader2, LogIn, Edit2, Trash2, Check, X, Save } from 'lucide-react'
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { CreateReviewInput } from '@buddys-choice/shared'
import type { CreateReviewInput as CreateReviewInputType } from '@buddys-choice/shared'

interface Review {
  id: string
  customerName: string
  customerPhone?: string | null
  rating: number
  body: string
  createdAt: string
}

interface ProductReviewsProps {
  productId: string
}

export const ProductReviews: React.FC<ProductReviewsProps> = ({ productId }) => {
  const { isCustomer, user } = useAuth()
  const [reviews, setReviews] = useState<Review[]>([])
  const [averageRating, setAverageRating] = useState(0)
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [submitError, setSubmitError] = useState('')

  // Edit / Delete state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editRating, setEditRating] = useState(5)
  const [editBody, setEditBody] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateReviewInputType>({
    resolver: zodResolver(CreateReviewInput),
    defaultValues: { productId, customerName: '', rating: 5, body: '' },
  })

  const loadReviews = async () => {
    try {
      const res = await apiGet<{ reviews: Review[]; averageRating: number }>('reviews', { productId })
      setReviews(res.reviews || [])
      setAverageRating(res.averageRating || 0)
    } catch {
      setReviews([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReviews()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId])

  useEffect(() => {
    if (user?.name) {
      setValue('customerName', user.name)
    }
  }, [user?.name, setValue])

  const onSubmit = async (data: CreateReviewInputType) => {
    if (!isCustomer) return
    setSubmitError('')
    setMessage('')
    try {
      await apiPost('reviews', data)
      setMessage('Thank you! Your review has been published.')
      reset({ productId, customerName: data.customerName, rating: 5, body: '' })
      await loadReviews()
    } catch (err: unknown) {
      setSubmitError((err as Error).message || 'Failed to submit review')
    }
  }

  const startEdit = (r: Review) => {
    setEditingId(r.id)
    setEditName(r.customerName)
    setEditRating(r.rating)
    setEditBody(r.body)
  }

  const cancelEdit = () => {
    setEditingId(null)
  }

  const handleSaveEdit = async (id: string) => {
    if (!editBody.trim()) return
    setSavingEdit(true)
    try {
      await apiPut(`reviews/${id}`, {
        customerName: editName,
        rating: editRating,
        body: editBody,
      })
      setEditingId(null)
      await loadReviews()
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to update review')
    } finally {
      setSavingEdit(false)
    }
  }

  const handleDeleteReview = async (id: string) => {
    setDeletingId(id)
    try {
      await apiDelete(`reviews/${id}`)
      setDeletingId(null)
      await loadReviews()
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to delete review')
      setDeletingId(null)
    }
  }

  const canManageReview = (r: Review) => {
    return !!(user?.phone && r.customerPhone && user.phone === r.customerPhone)
  }

  return (
    <section className="mt-10 space-y-6">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-[var(--gold-main)]" />
        <h2 className="text-lg font-title font-bold text-[var(--text-primary)]">Customer Reviews</h2>
        {averageRating > 0 && (
          <span className="text-xs text-[var(--gold-main)] flex items-center gap-1 ml-2 font-bold">
            <Star className="w-3.5 h-3.5 fill-current" />
            {averageRating} ({reviews.length})
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--gold-main)]" />
        </div>
      ) : reviews.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)]">No reviews yet. Be the first to share your experience!</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => {
            const isEditing = editingId === r.id
            const canManage = canManageReview(r)

            return (
              <div key={r.id} className="rounded-xl border border-[var(--border)] p-4 bg-[var(--bg-surface-2)] space-y-2">
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="form-input text-xs w-48"
                        placeholder="Name"
                      />
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setEditRating(n)}
                            className={`p-0.5 ${n <= editRating ? 'text-[var(--gold-main)]' : 'text-[var(--text-faint)]'}`}
                          >
                            <Star className={`w-4 h-4 ${n <= editRating ? 'fill-current' : ''}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={2}
                      className="form-input text-xs resize-none"
                    />
                    <div className="flex justify-end gap-2 text-xs">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="btn-gold-outline py-1 px-3 text-xs"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(r.id)}
                        disabled={savingEdit}
                        className="btn-gold py-1 px-3 text-xs flex items-center gap-1"
                      >
                        {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-semibold text-[var(--text-primary)]">{r.customerName}</span>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-0.5 text-[var(--gold-main)]">
                          {Array.from({ length: r.rating }).map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-current" />
                          ))}
                        </span>
                        {canManage && (
                          <div className="flex items-center gap-1 text-[var(--text-muted)]">
                            <button
                              type="button"
                              onClick={() => startEdit(r)}
                              className="p-1 hover:text-[var(--gold-main)] rounded transition"
                              title="Edit review"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteReview(r.id)}
                              disabled={deletingId === r.id}
                              className="p-1 hover:text-red-400 rounded transition"
                              title="Delete review"
                            >
                              {deletingId === r.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin text-red-400" />
                              ) : (
                                <Trash2 className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-[var(--text-muted)] leading-relaxed">{r.body}</p>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}

      {isCustomer ? (
        <form onSubmit={handleSubmit(onSubmit)} className="glass-luxury-card p-5 space-y-3">
          <h3 className="text-sm font-bold text-[var(--text-primary)]">Write a review</h3>
          <div>
            <input
              type="text"
              {...register('customerName')}
              placeholder="Your name"
              aria-invalid={!!errors.customerName}
              className="form-input text-xs"
            />
            {errors.customerName && <p className="form-error">{errors.customerName.message}</p>}
          </div>
          <Controller
            control={control}
            name="rating"
            render={({ field }) => (
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => field.onChange(n)}
                    className={`p-1 ${n <= field.value ? 'text-[var(--gold-main)]' : 'text-[var(--text-faint)]'}`}
                    aria-label={`Rate ${n} stars`}
                  >
                    <Star className={`w-5 h-5 ${n <= field.value ? 'fill-current' : ''}`} />
                  </button>
                ))}
              </div>
            )}
          />
          <div>
            <textarea
              {...register('body')}
              placeholder="Share your experience with this product…"
              rows={3}
              aria-invalid={!!errors.body}
              className="form-input text-xs resize-none"
            />
            {errors.body && <p className="form-error">{errors.body.message}</p>}
          </div>
          {submitError && <p className="form-error">{submitError}</p>}
          {message && <p className="text-xs text-emerald-500">{message}</p>}
          <button type="submit" disabled={isSubmitting} className="btn-gold text-xs py-2.5 px-5">
            {isSubmitting ? 'Submitting…' : 'Submit Review'}
          </button>
        </form>
      ) : (
        <div className="glass-luxury-card p-5 text-center space-y-3">
          <p className="text-xs text-[var(--text-muted)]">Sign in to write a review for this product.</p>
          <Link
            to="/account/login"
            search={{ redirect: `/product/${productId}` }}
            className="btn-gold text-xs py-2.5 px-5 inline-flex items-center gap-2"
          >
            <LogIn className="w-4 h-4" />
            Sign in
          </Link>
        </div>
      )}
    </section>
  )
}
